import { css } from 'lit';
import { hostReset } from '../styles/mixins.js';
import * as t from '../styles/tokens.js';

export default [
  hostReset,
  css`
    :host {
      display: inline-flex;
      align-items: center;
      gap: ${t.space2};
      vertical-align: middle;
      cursor: pointer;
      user-select: none;
    }
    :host([disabled]) {
      pointer-events: none;
      opacity: 0.6;
      cursor: not-allowed;
    }

    /* ── Box ────────────────────────────────────────────────────────────────── */
    .uwc-cb {
      /* Box size derives from the global font token (×1.286 ≈ the old 1.125rem
         at the 0.875rem default) so the checkbox rescales with the size/font
         presets. A theme can still pin an exact size via --uwc-checkbox-size. */
      --_size:   var(--uwc-checkbox-size,   calc(var(--uwc-font-size-md, 0.875rem) * 1.286));
      --_radius: var(--uwc-checkbox-radius, ${t.radiusSm});
      --_border: var(--uwc-checkbox-border, 1px solid ${t.borderControl});
      --_bg:     var(--uwc-checkbox-bg,     ${t.surface});
      --_color:  var(--uwc-checkbox-color,  var(--uwc-color-primary, ${t.primary}));

      position: relative;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width:  var(--_size);
      height: var(--_size);
      flex-shrink: 0;
      border:        var(--_border);
      border-radius: var(--_radius);
      background:    var(--_bg);
      transition:
        background-color ${t.durationBase},
        border-color     ${t.durationBase},
        box-shadow       ${t.durationBase};
    }

    /* Hidden native checkbox */
    input[type="checkbox"] {
      position: absolute;
      inset: 0;
      opacity: 0;
      width: 100%;
      height: 100%;
      margin: 0;
      cursor: pointer;
    }

    /* Checked / indeterminate state */
    :host([checked]) .uwc-cb,
    :host([indeterminate]) .uwc-cb {
      background:   var(--_color);
      border-color: var(--_color);
    }

    /* Checkmark icon */
    .uwc-cb__icon {
      display: none;
      pointer-events: none;
      color: var(--uwc-checkbox-icon-color, ${t.colorWhite});
      /* Track the box size so the glyph stays proportional at every scale. */
      width:  calc(var(--_size) * 0.61);
      height: calc(var(--_size) * 0.61);
    }
    :host([checked]) .uwc-cb__icon--check         { display: block; }
    :host([indeterminate]) .uwc-cb__icon--minus    { display: block; }

    /* Focus ring */
    input[type="checkbox"]:focus-visible ~ .uwc-cb,
    .uwc-cb:focus-within {
      box-shadow: 0 0 0 3px color-mix(in oklab, var(--_color) 25%, transparent);
    }

    /* Hover */
    :host(:not([disabled]):not([checked]):not([indeterminate])) .uwc-cb:hover {
      border-color: var(--_color);
    }

    /* ── Invalid ────────────────────────────────────────────────────────────── */
    :host([invalid]) .uwc-cb {
      border-color: var(--uwc-color-danger, ${t.danger});
    }

    /* ── Filled variant ─────────────────────────────────────────────────────── */
    :host([variant="filled"]:not([checked]):not([indeterminate])) .uwc-cb {
      --_bg: ${t.surfaceRaised};
      border-color: transparent;
    }

    /* ── Label ──────────────────────────────────────────────────────────────── */
    .uwc-cb__label {
      font-size:   var(--uwc-checkbox-font-size, ${t.fontSizeMd});
      color:       var(--uwc-checkbox-label-color, ${t.text});
      line-height: 1.4;
    }
  `,
];
